/**
 * Deterministic, zero-model summaries of commands and path lists for activity rows.
 *
 *   parseCommand("cd /wt && FOO=1 pnpm verify 2>&1 | tail -3", "/wt")
 *     → { title: "pnpm verify", env: ["FOO=1"], dir: undefined, full: <original> }
 *   summarizePaths([...12 reads under apps/daemon/src/setup])
 *     → { where: "in apps/daemon/src/setup", names: "cli.ts, doctor.ts +10" }
 */
import { unwrapShell } from '@viko/protocol';
const trimSlash = (p) => (p.length > 1 ? p.replace(/[\\/]+$/, '') : p);
const unquote = (s) => s.replace(/^(['"])(.*)\1$/, '$2');
/** `dir` relative to `root`: '' for the root itself, undefined when outside it. */
export function relativeTo(dir, root) {
    const d = trimSlash(unquote(dir));
    if (!root)
        return d.startsWith('/') || /^[A-Za-z]:\\/.test(d) ? undefined : d;
    const r = trimSlash(root);
    if (d === r || d === '.' || d === './')
        return '';
    if (d.startsWith(`${r}/`) || d.startsWith(`${r}\\`))
        return d.slice(r.length + 1);
    if (!d.startsWith('/') && !/^[A-Za-z]:\\/.test(d) && !d.startsWith('~'))
        return d.replace(/^\.\//, '');
    return undefined;
}
const CD = /^\s*(?:cd|pushd)\s+("[^"]*"|'[^']*'|[^\s;&|]+)\s*(?:&&|;)\s*/;
const ENV = /^\s*([A-Za-z_][A-Za-z0-9_]*=(?:"[^"]*"|'[^']*'|[^\s"']*))\s+/;
const NOISE = [
    /\s*2>&1\s*$/,
    /\s*\|\s*(?:tail|head)(?:\s+-n)?(?:\s+-?\d+)?\s*$/,
    /\s*;\s*echo\s+["']?(?:EXIT|exit)[^;|&]*\$\?["']?\s*$/,
    /\s*\|\|\s*true\s*$/,
];
export function parseCommand(command, root) {
    const full = unwrapShell(command).trim();
    let rest = full;
    let dir;
    for (let m = CD.exec(rest); m; m = CD.exec(rest)) {
        const target = m[1] ?? '';
        const rel = relativeTo(target, root);
        const base = dir && rel !== undefined && !target.startsWith('/') ? `${dir}/${rel}` : rel;
        dir = base ?? trimSlash(unquote(target));
        rest = rest.slice(m[0].length);
    }
    const env = [];
    for (let m = ENV.exec(rest); m; m = ENV.exec(rest)) {
        env.push(m[1] ?? '');
        rest = rest.slice(m[0].length);
    }
    let changed = true;
    while (changed) {
        changed = false;
        for (const re of NOISE) {
            const next = rest.replace(re, '');
            if (next !== rest && next.trim()) {
                rest = next;
                changed = true;
            }
        }
    }
    return {
        title: rest.trim() || full,
        ...(dir ? { dir } : {}),
        env,
        full,
    };
}
// ---------------------------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------------------------
/** Small override table: path prefix → friendly area name. */
const AREA_OVERRIDES = [
    [/^\.claude\/tasks(\/|$)/, 'task notes'],
    [/^\.github(\/|$)/, 'CI config'],
    [/^docs?(\/|$)/, 'docs'],
    [/^fixtures?(\/|$)/, 'fixtures'],
    [/^(tests?|__tests__|spec)(\/|$)/, 'tests'],
    [/^scripts?(\/|$)/, 'scripts'],
];
const stripExt = (s) => s.replace(/\.[^.]+$/, '');
/** Friendly name of the repo area a relative path is in ("daemon setup", "protocol package"). */
export function pathArea(rel) {
    for (const [re, name] of AREA_OVERRIDES)
        if (re.test(rel))
            return name;
    const parts = rel.split('/').filter(Boolean);
    const [top, pkg, sub, leaf] = parts;
    if ((top === 'apps' || top === 'packages' || top === 'tools') && pkg && parts.length > 2) {
        const noun = top === 'packages' ? `${pkg} package` : pkg;
        if (sub === 'test' || sub === 'tests' || sub === '__tests__' || sub === 'e2e')
            return `${pkg} tests`;
        if (top === 'apps' && sub === 'src' && leaf && parts.length > 4)
            return `${pkg} ${leaf}`;
        return noun;
    }
    if (parts.length <= 1)
        return 'repo root';
    return stripExt(top ?? '');
}
const basename = (p) => p.split(/[\\/]/).filter(Boolean).at(-1) ?? p;
function commonDir(paths) {
    const split = paths.map((p) => p.split('/').slice(0, -1));
    const first = split[0] ?? [];
    let n = 0;
    while (n < first.length && split.every((s) => s[n] === first[n]))
        n++;
    return first.slice(0, n).join('/');
}
/**
 * Group a list of file paths for a one-line summary: a shared directory when there is a
 * meaningful one, else the (up to three) repo areas they fall in, most-touched first.
 */
export function summarizePaths(input, root, show = 2) {
    const paths = [...new Set(input.map((p) => relativeTo(p, root) ?? p))];
    const count = paths.length;
    const names = paths.slice(0, show).map(basename).join(', ') + (count > show ? ` +${count - show}` : '');
    if (count <= 1)
        return { count, where: '', names, paths };
    const dir = commonDir(paths.filter((p) => !p.startsWith('/')));
    if (dir && dir.split('/').length >= 2 && paths.every((p) => p.startsWith(`${dir}/`)))
        return { count, where: `in ${dir}`, names, paths };
    const areas = new Map();
    for (const p of paths) {
        const a = p.startsWith('/') ? 'outside the repo' : pathArea(p);
        areas.set(a, (areas.get(a) ?? 0) + 1);
    }
    const ranked = [...areas.entries()].sort((a, b) => b[1] - a[1]).map(([a]) => a);
    const where = ranked.length === 1
        ? ranked[0] === 'repo root'
            ? ''
            : `in ${ranked[0]}`
        : `· ${ranked.slice(0, 3).join(', ')}${ranked.length > 3 ? ` +${ranked.length - 3} more` : ''}`;
    return { count, where, names, paths };
}
/** Match counts from a search tool's output (Claude Grep/Glob, rg, grep -r). */
export function searchCounts(output) {
    if (!output)
        return {};
    const found = /^Found (\d+) (?:files?|matches?)/m.exec(output);
    if (found)
        return { files: Number(found[1]) };
    const lines = output.split('\n').filter((l) => l.trim() && !/^No (?:files|matches) found/.test(l));
    if (!lines.length)
        return { matches: 0, files: 0 };
    const files = new Set(lines.map((l) => /^([^:\s]+):\d+:/.exec(l)?.[1] ?? /^([^:\s]+\.[\w]+)$/.exec(l.trim())?.[1]));
    files.delete(undefined);
    return { matches: lines.length, ...(files.size ? { files: files.size } : {}) };
}
//# sourceMappingURL=summarize.js.map